import MapboxDraw, { DrawCustomMode, DrawCustomModeThis, DrawFeature, DrawLineString, DrawPoint } from "@mapbox/mapbox-gl-draw";
import booleanEqual from "@turf/boolean-equal";
import { Position } from "geojson";
import { isEqual } from "lodash";
import mapboxgl from "mapbox-gl";
import { Utils } from "mapbox-gl-draw-snap-mode";

import { DRAWING_CONSTANTS } from "fond/constants";
import { enumerate } from "fond/utils";

import simpleSelect from "../../base/simpleSelect";

import { getFeatureId, getSpanPoles, pullInFeatures, refreshSnappedStatus, splitPaths, validSpan } from "./helper";

const { snap, createSnapList } = Utils;

const { modes } = MapboxDraw;

interface DrawCustomModeState {
  poleDragInit: {
    connectedSpans: DrawLineString[];
    connectedIds: Set<string | number>;
  } | null;
  snapList: mapboxgl.MapboxGeoJSONFeature[];
  vertices: mapboxgl.MapboxGeoJSONFeature[];
  boxSelecting: boolean;
}

interface DrawCustomModeExtension<S> extends DrawCustomMode<S> {
  dragMove(this: DrawCustomModeThis, state: S, event: MapboxDraw.MapMouseEvent): void;
}

/**
 * This is a custom mode for selecting and dragging poles in the aerial planner.
 * It extends the simple_select mode and adds functionality for snapping to spans.
 */
const spanPoleSelect: DrawCustomModeExtension<DrawCustomModeState> = {
  ...simpleSelect,

  onSetup: function (opts) {
    const state = modes.simple_select.onSetup?.call(this, opts);
    state.options = this._ctx.options;
    state.map = this.map;

    return state;
  },

  /**
   * @param state - The current state of the mode.
   * @param e - The mouse event.
   * @description
   * Monitors the mouse down event & if the user has clicked on a pole we create a list
   * of features the pole can snap to during the onDrag event
   *
   * Note: We only allow poles to be selected & dragged, spans (and their vertices) cannot
   * be dragged.
   */
  onMouseDown: function (state, e) {
    if (!e.featureTarget || e.featureTarget.geometry.type === "LineString") {
      // We don't want to be able to move spans without moving poles.
      simpleSelect.onMouseDown?.call(this, state, e);
    } else {
      const featureId = getFeatureId(e.featureTarget);

      if (e.featureTarget != null && featureId) {
        const pole = this.getFeature(featureId) as DrawPoint;
        const connectedIds = new Set<string | number>();
        const { spans } = pullInFeatures.call(this, pole, connectedIds, e.type);

        refreshSnappedStatus.call(this, pole);

        for (let span of spans) {
          const spanId = getFeatureId(span);
          if (spanId) connectedIds.add(spanId);
        }

        // We need to filter out the current poles connected spans as we don't want to allow snapping to them
        const snapGetFeatures = (_: mapboxgl.Map, draw: MapboxDraw) =>
          draw.getAll().features.filter((feature) => !connectedIds.has(getFeatureId(feature)));
        const [snapList, vertices] = createSnapList(this.map, this._ctx.api, pole, snapGetFeatures);

        // Mutate the local state with the available snapping information
        state.snapList = snapList;
        state.vertices = vertices;
        state.poleDragInit = {
          connectedSpans: spans,
          connectedIds: connectedIds,
        };
      }

      simpleSelect.onMouseDown?.call(this, state, e);
    }
  },

  /**
   * On mouse up we need to check:
   * 1) Is the pole on top of another & is so remove the current pole to avoid duplicate collocated poles;
   * 2) Did the pole snap to a span? if so create a new vertex at the pole location;
   * 3) Confirm if any spans are collocated with any of the new span locations. If so delete the collocated duplicates.
   */
  onMouseUp: function (state, e) {
    const selected = this.getSelected();
    if (selected.length === 1 && !state.boxSelecting && selected[0].type === "Point") {
      const featureId = getFeatureId(selected[0]);
      const { spans, poles } = pullInFeatures.call(this, selected[0], state.poleDragInit?.connectedIds, e.type);

      if (poles.length > 0) {
        // Delete the pole currently being dragged
        this.deleteFeature(featureId, { silent: false });

        state.poleDragInit?.connectedSpans.forEach((original: DrawLineString) => {
          // Confirm if any of the existing connected spans are now invalid
          // e.g. the span has vertices at the same coordinates (pole dropped on another pole)
          if (!validSpan(original)) this.deleteFeature(original.id, { silent: false });

          // Confirm if any of the existing connected Spans are now collocated
          spans.forEach((span: DrawLineString) => {
            if (booleanEqual(span, original, { precision: 6 })) {
              this.deleteFeature(span.id, { silent: false });
            }
          });
        });

        // Refresh snapped status of existing poles as this may have changed
        // due to two poles being dropped on top of each other.
        poles.forEach((pole) => {
          refreshSnappedStatus.call(this, pole);
        });
      } else if (spans.length > 0) {
        const newPaths = splitPaths.call(this, spans[0], selected[0]);

        // Confirm if any of the existing connected Spans are now collocated
        // with the new split paths
        state.poleDragInit?.connectedSpans.forEach((original: DrawLineString) => {
          newPaths.forEach((span: DrawLineString) => {
            if (booleanEqual(original, span, { precision: 6 })) {
              this.deleteFeature(original.id, { silent: false });
            }
          });
        });
      }

      // Update the snapped status of the pole being moved
      const isSnapped = (state.poleDragInit && state.poleDragInit.connectedIds.size > 0) || spans.length > 0;
      selected[0].setProperty(DRAWING_CONSTANTS.Unsnapped, Boolean(!isSnapped).toString());

      this.map.fire("draw.update", { features: selected });
    }

    return simpleSelect.onMouseUp?.call(this, state, e);
  },

  /**
   * @param state - The current state of the mode.
   * @param e - The mouse event.
   * @description
   * Handles the drag move event for the span pole select mode.
   * It updates the coordinates of the selected pole and moves any connected spans accordingly.
   */
  dragMove: function (state, e) {
    // Dragging when drag move is enabled
    e.originalEvent.stopPropagation();
    const selected = this.getSelected() as DrawFeature[];

    // We do not allow multiple items to be moved or paths
    if (selected.length > 1 || selected?.[0].type === "LineString") return;

    // If the user is dragging a pole with connected spans we need to update the coordinates of the spans
    // to match the new coordinates of the pole.
    const moveLines = (oldCoords: Position, newCoords: Position) => {
      if (state.poleDragInit?.connectedSpans) {
        for (let f of state.poleDragInit.connectedSpans) {
          const featureId = getFeatureId(f);
          if (featureId) {
            const feature = this.getFeature(featureId) as DrawLineString;
            for (let [i, c] of enumerate(feature.coordinates)) {
              if (isEqual(c, oldCoords)) {
                feature.incomingCoords(feature.coordinates.toSpliced(i, 1, newCoords));
                break;
              }
            }
          }
        }
      }
    };

    // Get the new coordinates of the pole and update the coordinates of the spans
    // connected to the pole.
    const oldCoords = selected[0].getCoordinates() as Position;
    const { lng, lat } = snap(state, e);
    selected[0].updateCoordinate("", lng, lat);

    const newCoords = selected[0].getCoordinates() as Position;
    moveLines(oldCoords, newCoords);
  },

  onStop: function (state) {
    simpleSelect.onStop?.call(this, state);
    state.poleDragInit = null;
  },

  /**
   * @description
   * This function is called when the user clicks the trash button.
   * It deletes the selected features and any connected spans.
   * It also refreshes the snapped status of any connected poles.
   */
  onTrash: function () {
    const selected = this.getSelected();
    const deleteIds: string[] = [];
    const polesToRefresh: DrawPoint[] = [];

    selected.forEach((feature) => {
      deleteIds.push(feature.id);
      if (feature.type === "Point") {
        const { spans } = pullInFeatures.call(this, feature);
        // If deleting a pole we delete all spans connected to it.
        deleteIds.push(...spans.map(getFeatureId));
      } else if (feature.type === "LineString") {
        const poles = getSpanPoles.call(this, feature);
        polesToRefresh.push(...poles);
      }
    });

    this.deleteFeature(deleteIds);

    polesToRefresh.forEach((pole) => {
      refreshSnappedStatus.call(this, pole);
    });
  },
};

export default spanPoleSelect;
